import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentType, CSSProperties, FormEvent, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded'
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CorporateFareRoundedIcon from '@mui/icons-material/CorporateFareRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'
import WorkRoundedIcon from '@mui/icons-material/WorkRounded'
import { suggestWorkspaceSlug, validateWorkspaceSlug } from '../../utils/workspaceSlug'
import timeliningLogo from '../../assets/logo.png'
import { InteractiveConstellation } from '../Common/InteractiveConstellation'
import { FilterChip, Textarea, TextInput, Toggle } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { WorkspaceCurrencyPickerStep } from './WorkspaceCurrencyPickerStep'
import { SetupStepFooter } from './SetupStepFooter'
import { detectLocaleCurrency } from '../../utils/localeCurrency'
import { useNls } from '../../nls/nlsProvider'
import type { STRINGS_NLS } from '../../nls/strings_nls'
import {
  resolveUseCaseFromAudienceParam,
  WORKSPACE_USE_CASE_OPTIONS,
  type WorkspaceUseCaseOption,
} from '../../utils/workspaceModules'

import {
  DEFAULT_WORKSPACE_SETUP_DRAFT,
  PERSONAL_STEP_ORDER,
  type StepId,
  stubInferFromFreeform,
  TEAM_STEP_ORDER,
  type WorkspaceAudience,
  type WorkspaceFocusArea,
  type WorkspaceRoleTitle,
  type WorkspaceSetupDraft,
  type WorkspaceTeamSize,
  type WorkspaceThemePref,
  type WorkspaceUseCase,
} from './workspaceSetupStepConfig'

export type {
  StepId,
  WorkspaceAudience,
  WorkspaceFocusArea,
  WorkspaceRoleTitle,
  WorkspaceSetupDraft,
  WorkspaceTeamSize,
  WorkspaceThemePref,
  WorkspaceUseCase,
}

type WorkspaceSetupOnboardingProps = {
  initialDraft?: Partial<WorkspaceSetupDraft>
  onCancel?: () => void
  onComplete?: (draft: WorkspaceSetupDraft) => void
  onCheckWorkspaceSlug?: (slug: string) => Promise<{ available: boolean; error: string | null }>
  setupKind?: 'personal' | 'team'
  isCompleting?: boolean
  onParseFreeformIntent?: (userInput: string) => Promise<Partial<WorkspaceSetupDraft>>
}

const ROLE_OPTIONS: { value: WorkspaceRoleTitle; labelKey: keyof typeof STRINGS_NLS }[] = [
  { value: 'Founder / CEO', labelKey: 'NLSSetupRoleFounder' },
  { value: 'Product Manager', labelKey: 'NLSSetupRolePM' },
  { value: 'Engineer', labelKey: 'NLSSetupRoleEngineer' },
  { value: 'Designer', labelKey: 'NLSSetupRoleDesigner' },
  { value: 'Marketer', labelKey: 'NLSSetupRoleMarketer' },
  { value: 'Operator', labelKey: 'NLSSetupRoleOperator' },
  { value: 'Student', labelKey: 'NLSSetupRoleStudent' },
  { value: 'Researcher', labelKey: 'NLSSetupRoleResearcher' },
  { value: 'Creator', labelKey: 'NLSSetupRoleCreator' },
  { value: 'Other', labelKey: 'NLSSetupRoleOther' },
]

const USE_CASE_ICONS: Record<string, ComponentType<SvgIconProps>> = {
  person: PersonRoundedIcon,
  groups: GroupsRoundedIcon,
  school: SchoolRoundedIcon,
  content: CampaignRoundedIcon,
  startup: RocketLaunchRoundedIcon,
  other: AutoAwesomeRoundedIcon,
  field: HandymanRoundedIcon,
  shop: StorefrontRoundedIcon,
  events: EventRoundedIcon,
  school_edu: SchoolRoundedIcon,
  coaching: MenuBookRoundedIcon,
  academy: SchoolRoundedIcon,
  corporate_training: BusinessCenterRoundedIcon,
  agency: CorporateFareRoundedIcon,
}

const TEAM_SIZE_OPTIONS: { value: WorkspaceTeamSize; labelKey: keyof typeof STRINGS_NLS; hintKey: keyof typeof STRINGS_NLS }[] = [
  { value: 'SMALL', labelKey: 'NLSSetupTeamSizeSmallLabel', hintKey: 'NLSSetupTeamSizeSmallHint' },
  { value: 'MEDIUM', labelKey: 'NLSSetupTeamSizeMediumLabel', hintKey: 'NLSSetupTeamSizeMediumHint' },
  { value: 'LARGE', labelKey: 'NLSSetupTeamSizeLargeLabel', hintKey: 'NLSSetupTeamSizeLargeHint' },
]

const FOCUS_OPTIONS: { value: WorkspaceFocusArea; labelKey: keyof typeof STRINGS_NLS; Icon: ComponentType<SvgIconProps> }[] = [
  { value: 'PRODUCTIVITY', labelKey: 'NLSSetupFocusProductivity', Icon: BoltRoundedIcon },
  { value: 'LEARNING', labelKey: 'NLSSetupFocusLearning', Icon: SchoolRoundedIcon },
  { value: 'HEALTH', labelKey: 'NLSSetupFocusHealth', Icon: FavoriteRoundedIcon },
  { value: 'CAREER', labelKey: 'NLSSetupFocusCareer', Icon: WorkRoundedIcon },
  { value: 'SIDE_PROJECTS', labelKey: 'NLSSetupFocusSideProjects', Icon: LightbulbRoundedIcon },
  { value: 'CREATIVE', labelKey: 'NLSSetupFocusCreative', Icon: PaletteRoundedIcon },
  { value: 'RESEARCH', labelKey: 'NLSSetupFocusResearch', Icon: ScienceRoundedIcon },
]

const THEME_OPTIONS: { value: WorkspaceThemePref; labelKey: keyof typeof STRINGS_NLS; hintKey: keyof typeof STRINGS_NLS }[] = [
  { value: 'DARK', labelKey: 'NLSSetupThemeDarkLabel', hintKey: 'NLSSetupThemeDarkHint' },
  { value: 'LIGHT', labelKey: 'NLSSetupThemeLightLabel', hintKey: 'NLSSetupThemeLightHint' },
  { value: 'SYSTEM', labelKey: 'NLSSetupThemeSystemLabel', hintKey: 'NLSSetupThemeSystemHint' },
]

type StepHeadlineKey = {
  eyebrowKey: keyof typeof STRINGS_NLS
  questionKey: keyof typeof STRINGS_NLS
  subKey: keyof typeof STRINGS_NLS
}

const STEP_HEADLINE_KEYS: Record<StepId, StepHeadlineKey> = {
  INTENT: {
    eyebrowKey: 'NLSSetupStepIntentEyebrow',
    questionKey: 'NLSSetupStepIntentQuestion',
    subKey: 'NLSSetupStepIntentSub',
  },
  ROLE: {
    eyebrowKey: 'NLSSetupStepRoleEyebrow',
    questionKey: 'NLSSetupStepRoleQuestion',
    subKey: 'NLSSetupStepRoleSub',
  },
  USE_CASE: {
    eyebrowKey: 'NLSSetupStepUseCaseEyebrow',
    questionKey: 'NLSSetupStepUseCaseQuestion',
    subKey: 'NLSSetupStepUseCaseSub',
  },
  CURRENCY: {
    eyebrowKey: 'NLSSetupStepCurrencyEyebrow',
    questionKey: 'NLSSetupStepCurrencyQuestion',
    subKey: 'NLSSetupStepCurrencySub',
  },
  WHO: {
    eyebrowKey: 'NLSSetupStepWhoEyebrow',
    questionKey: 'NLSSetupStepWhoQuestion',
    subKey: 'NLSSetupStepWhoSub',
  },
  FOCUS_AREAS: {
    eyebrowKey: 'NLSSetupStepFocusAreasEyebrow',
    questionKey: 'NLSSetupStepFocusAreasQuestion',
    subKey: 'NLSSetupStepFocusAreasSub',
  },
  APPEARANCE: {
    eyebrowKey: 'NLSSetupStepAppearanceEyebrow',
    questionKey: 'NLSSetupStepAppearanceQuestion',
    subKey: 'NLSSetupStepAppearanceSub',
  },
  EXTRAS: {
    eyebrowKey: 'NLSSetupStepExtrasEyebrow',
    questionKey: 'NLSSetupStepExtrasQuestion',
    subKey: 'NLSSetupStepExtrasSub',
  },
  WORKSPACE_NAME: {
    eyebrowKey: 'NLSSetupStepWorkspaceNameEyebrow',
    questionKey: 'NLSSetupStepWorkspaceNameQuestion',
    subKey: 'NLSSetupStepWorkspaceNameSub',
  },
}

const FREEFORM_SUGGESTION_KEYS: (keyof typeof STRINGS_NLS)[] = [
  'NLSSetupSuggestionMarathon',
  'NLSSetupSuggestionPodcast',
  'NLSSetupSuggestionPhd',
  'NLSSetupSuggestionStartup',
  'NLSSetupSuggestionSemester',
]

const slideVariants = {
  enter: (direction: number) => ({ opacity: 0, x: 60 * direction, filter: 'blur(6px)' }),
  center: { opacity: 1, x: 0, filter: 'blur(0px)' },
  exit: (direction: number) => ({ opacity: 0, x: -60 * direction, filter: 'blur(6px)' }),
}

export const WorkspaceSetupOnboarding = ({
  initialDraft,
  onCancel,
  onComplete,
  isCompleting = false,
  onCheckWorkspaceSlug,
  setupKind = 'team',
  onParseFreeformIntent,
}: WorkspaceSetupOnboardingProps) => {
  const { t } = useNls()

  const stepOrder = useMemo<StepId[]>(
    () => (setupKind === 'personal' ? PERSONAL_STEP_ORDER : TEAM_STEP_ORDER),
    [setupKind],
  )

  const [draft, setDraft] = useState<WorkspaceSetupDraft>(() => {
    let preselectedUseCase: WorkspaceUseCase | '' = ''
    if (setupKind === 'team' && typeof window !== 'undefined') {
      const forParam = new URLSearchParams(window.location.search).get('for')
      const matched = resolveUseCaseFromAudienceParam(forParam)
      if (matched && WORKSPACE_USE_CASE_OPTIONS.some((opt) => opt.value === matched)) {
        preselectedUseCase = matched
      }
    }

    return {
      ...DEFAULT_WORKSPACE_SETUP_DRAFT,
      currency: initialDraft?.currency || detectLocaleCurrency(),
      ...(preselectedUseCase ? { useCase: preselectedUseCase } : {}),
      ...(setupKind === 'personal'
        ? {
            workspaceName: 'Personal Space',
            workspaceSlug: '',
            audience: 'SOLO' as WorkspaceAudience,
            teamSize: 'SOLO' as WorkspaceTeamSize,
          }
        : {}),
      ...initialDraft,
    }
  })

  const [stepIndex, setStepIndex] = useState(() => {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('currency') === 'picker') {
      const targetList = setupKind === 'personal' ? PERSONAL_STEP_ORDER : TEAM_STEP_ORDER
      const idx = targetList.indexOf('CURRENCY')
      if (idx !== -1) return idx
    }
    return 0
  })
  const [direction, setDirection] = useState(1)
  const [freeformText, setFreeformText] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle')
  const [slugMessage, setSlugMessage] = useState<string | null>(null)

  const shellRef = useRef<HTMLDivElement | null>(null)
  const activeStep = stepOrder[stepIndex]
  const headlineKeys = activeStep ? STEP_HEADLINE_KEYS[activeStep] : null
  const progressPercent = Math.round(((stepIndex + 1) / stepOrder.length) * 100)

  useEffect(() => {
    const el = shellRef.current
    if (!el) return
    const onMove = (event: PointerEvent) => {
      const r = el.getBoundingClientRect()
      const mx = (event.clientX - r.left) / r.width
      const my = (event.clientY - r.top) / r.height
      el.style.setProperty('--spotlightX', `${(mx * 100).toFixed(1)}%`)
      el.style.setProperty('--spotlightY', `${(my * 100).toFixed(1)}%`)
    }
    el.addEventListener('pointermove', onMove)
    return () => el.removeEventListener('pointermove', onMove)
  }, [])

  const updateDraft = useCallback(
    <K extends keyof WorkspaceSetupDraft>(key: K, value: WorkspaceSetupDraft[K]) => {
      setDraft((current) => {
        if (key === 'workspaceName' && !current.workspaceSlug.trim()) {
          return { ...current, [key]: value, workspaceSlug: suggestWorkspaceSlug(String(value)) }
        }
        return { ...current, [key]: value }
      })
    },
    [],
  )

  const mergeDraft = useCallback((partial: Partial<WorkspaceSetupDraft>) => {
    setDraft((current) => ({ ...current, ...partial }))
  }, [])

  const completeSetup = useCallback(
    (nextDraft: WorkspaceSetupDraft = draft) => {
      const completedDraft: WorkspaceSetupDraft =
        setupKind === 'personal'
          ? {
              ...nextDraft,
              workspaceName: 'Personal Space',
              workspaceSlug: '',
              audience: 'SOLO',
              teamSize: 'SOLO',
            }
          : nextDraft
      onComplete?.(completedDraft)
    },
    [draft, onComplete, setupKind],
  )

  const advance = useCallback(() => {
    setDirection(1)
    const next = stepIndex + 1
    if (next >= stepOrder.length) {
      completeSetup()
      return
    }
    setStepIndex(next)
  }, [stepIndex, stepOrder.length, completeSetup])

  const goBack = useCallback(() => {
    if (stepIndex === 0) return
    setDirection(-1)
    setStepIndex(stepIndex - 1)
  }, [stepIndex])

  // Slug validation runs whenever we're on the WORKSPACE_NAME step and the slug changes.
  useEffect(() => {
    if (activeStep !== 'WORKSPACE_NAME') return
    const slug = draft.workspaceSlug.trim()
    const validationMessage = validateWorkspaceSlug(slug)
    if (validationMessage) {
      setSlugStatus(slug ? 'unavailable' : 'idle')
      setSlugMessage(validationMessage)
      return
    }
    if (!onCheckWorkspaceSlug) {
      setSlugStatus('available')
      setSlugMessage(t('NLSSetupSlugHintFormatValid'))
      return
    }

    setSlugStatus('checking')
    setSlugMessage(t('NLSSetupSlugHintChecking'))
    let isCancelled = false
    const timeout = window.setTimeout(() => {
      void onCheckWorkspaceSlug(slug)
        .then((result) => {
          if (isCancelled) return
          setSlugStatus(result.available ? 'available' : 'unavailable')
          setSlugMessage(
            result.available
              ? t('NLSSetupSlugHintAvailable')
              : (result.error ?? t('NLSSetupSlugHintUnavailable')),
          )
        })
        .catch(() => {
          if (isCancelled) return
          setSlugStatus('unavailable')
          setSlugMessage(t('NLSSetupSlugHintError'))
        })
    }, 550)
    return () => {
      isCancelled = true
      window.clearTimeout(timeout)
    }
  }, [activeStep, draft.workspaceSlug, onCheckWorkspaceSlug, t])

  const handleFreeformSubmit = useCallback(
    async (event?: FormEvent) => {
      event?.preventDefault()
      const trimmed = freeformText.trim()
      if (!trimmed) {
        advance()
        return
      }
      setIsThinking(true)
      try {
        const inferred = onParseFreeformIntent
          ? await onParseFreeformIntent(trimmed)
          : stubInferFromFreeform(trimmed)
        mergeDraft({ ...inferred, intentText: trimmed })
      } catch {
        mergeDraft({ intentText: trimmed })
      } finally {
        setIsThinking(false)
        advance()
      }
    },
    [freeformText, onParseFreeformIntent, mergeDraft, advance],
  )

  const handleFreeformKey = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void handleFreeformSubmit()
    }
  }

  const handlePickRole = (role: WorkspaceRoleTitle) => {
    updateDraft('roleTitle', role)
    window.setTimeout(advance, 140)
  }

  const handlePickUseCase = (useCase: WorkspaceUseCase) => {
    updateDraft('useCase', useCase)
    window.setTimeout(advance, 140)
  }

  const handlePickWho = (audience: WorkspaceAudience) => {
    if (audience === 'SOLO') {
      mergeDraft({ audience: 'SOLO', teamSize: 'SOLO' })
      window.setTimeout(advance, 140)
    } else if (audience === 'SOLO_HELPERS') {
      mergeDraft({ audience: 'SOLO_HELPERS', teamSize: 'SMALL' })
      window.setTimeout(advance, 140)
    } else {
      updateDraft('audience', 'TEAM')
    }
  }

  const handlePickTeamSize = (size: WorkspaceTeamSize) => {
    updateDraft('teamSize', size)
    window.setTimeout(advance, 140)
  }

  const toggleFocusArea = (focus: WorkspaceFocusArea) => {
    setDraft((current) => {
      const has = current.focusAreas.includes(focus)
      const nextAreas = has ? current.focusAreas.filter((value) => value !== focus) : [...current.focusAreas, focus]
      return { ...current, focusAreas: nextAreas }
    })
  }

  const handleNameSubmit = (event: FormEvent) => {
    event.preventDefault()
    const name = draft.workspaceName.trim()
    if (!name) return
    if (validateWorkspaceSlug(draft.workspaceSlug) || slugStatus !== 'available') return
    completeSetup({ ...draft, workspaceName: name, workspaceSlug: draft.workspaceSlug.trim() })
  }

  const useCaseOptions = useMemo(
    () =>
      setupKind === 'personal'
        ? WORKSPACE_USE_CASE_OPTIONS.filter((option) => !option.teamOnly)
        : WORKSPACE_USE_CASE_OPTIONS,
    [setupKind],
  )

  const summary = useMemo(() => {
    const focusLabels = draft.focusAreas
      .map((focus) => FOCUS_OPTIONS.find((option) => option.value === focus))
      .filter(Boolean) as { value: WorkspaceFocusArea; labelKey: keyof typeof STRINGS_NLS; Icon: ComponentType<SvgIconProps> }[]
    const useCaseOption = WORKSPACE_USE_CASE_OPTIONS.find((option) => option.value === draft.useCase)
    const useCaseIcon = useCaseOption ? (USE_CASE_ICONS[useCaseOption.iconKey] ?? AutoAwesomeRoundedIcon) : AutoAwesomeRoundedIcon
    const teamSizeOption = TEAM_SIZE_OPTIONS.find((option) => option.value === draft.teamSize)
    return { focusLabels, useCaseOption, useCaseIcon, teamSizeOption }
  }, [draft.focusAreas, draft.useCase, draft.teamSize])

  const workspaceInitial = (draft.workspaceName.trim() || (setupKind === 'personal' ? 'P' : 'W'))
    .charAt(0)
    .toUpperCase()

  return (
    <main className="setupOnboarding" aria-label={t('NLSSetupMainAria')}>
      <InteractiveConstellation
        className="setupOnboarding__constellation"
        density={2200}
        linkDistance={124}
        interactionRadius={170}
        intensity={0.85}
        opacity={0.55}
      />
      <div className="setupOnboarding__vignette" aria-hidden="true" />

      <motion.section
        className="setupShell"
        ref={shellRef}
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 110, damping: 22 }}
        style={{
          '--spotlightX': '50%',
          '--spotlightY': '0%',
        } as CSSProperties}
      >
        <span className="setupShell__spotlight" aria-hidden="true" />

        <header className="setupTopBar">
          <div className="setupBrand">
            <img src={timeliningLogo} alt="" aria-hidden="true" />
            <span>{t('NLSSetupBrandName')}</span>
          </div>
          <div className="setupProgress" aria-label={t('NLSSetupProgressAria', { current: stepIndex + 1, total: stepOrder.length })}>
            <span className="setupProgress__label">
              {t('NLSSetupProgressStep', { current: stepIndex + 1, total: stepOrder.length })}
            </span>
            <span className="setupProgress__bar">
              <span className="setupProgress__fill" style={{ width: `${progressPercent}%` }} />
            </span>
          </div>
          {onCancel && (
            <Button
              variant="tertiary"
              size="sm"
              icon={<CloseRoundedIcon />}
              onClick={onCancel}
              disabled={isCompleting}
            >
              {t('NLSSetupActionCancel')}
            </Button>
          )}
        </header>

        {isCompleting ? (
          <CompletionState setupKind={setupKind} name={draft.workspaceName} />
        ) : (
          <div className="setupBody">
            <section className="setupStage" aria-live="polite">
              <AnimatePresence mode="wait" custom={direction} initial={false}>
                <motion.article
                  key={activeStep}
                  className="setupCard"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
                >
                  {activeStep === 'CURRENCY' ? (
                    <WorkspaceCurrencyPickerStep
                      value={draft.currency}
                      onChange={(currency) => updateDraft('currency', currency)}
                      onBack={goBack}
                      onContinue={(currency) => {
                        updateDraft('currency', currency)
                        advance()
                      }}
                    />
                  ) : (
                    <>
                      <header className="setupCard__head">
                        <span className="setupCard__eyebrow">{headlineKeys ? t(headlineKeys.eyebrowKey) : ''}</span>
                        <h2 className="setupCard__question">{headlineKeys ? t(headlineKeys.questionKey) : ''}</h2>
                        <p className="setupCard__sub">{headlineKeys ? t(headlineKeys.subKey) : ''}</p>
                      </header>

                      <div className="setupCard__body">
                        {activeStep === 'INTENT' && (
                          <form className="setupFreeform" onSubmit={handleFreeformSubmit}>
                            <Textarea
                              value={freeformText}
                              onChange={(next) => setFreeformText(next)}
                              onKeyDown={handleFreeformKey}
                              placeholder={t('NLSSetupFreeformPlaceholder')}
                              rows={4}
                              autoFocus
                            />
                            <div className="setupFreeform__suggest">
                              {FREEFORM_SUGGESTION_KEYS.map((key) => {
                                const chipText = t(key)
                                return (
                                  <FilterChip
                                    key={key}
                                    selected={freeformText === chipText}
                                    onClick={() => setFreeformText(chipText)}
                                  >
                                    {chipText}
                                  </FilterChip>
                                )
                              })}
                            </div>
                            {isThinking && (
                              <div className="setupThinking" aria-live="polite">
                                <span className="setupThinkingDots"><span /><span /><span /></span>
                                <span>{t('NLSSetupThinkingSub')}</span>
                              </div>
                            )}
                          </form>
                        )}

                        {activeStep === 'ROLE' && (
                          <div className="setupChoiceGrid setupChoiceGrid--tight">
                            {ROLE_OPTIONS.map((role) => (
                              <FilterChip
                                key={role.value}
                                selected={draft.roleTitle === role.value}
                                onClick={() => handlePickRole(role.value)}
                              >
                                {t(role.labelKey)}
                              </FilterChip>
                            ))}
                          </div>
                        )}

                        {activeStep === 'USE_CASE' && (
                          <div className="setupChoiceGrid setupChoiceGrid--cards">
                            {useCaseOptions.map((option: WorkspaceUseCaseOption) => {
                              const IconComp = USE_CASE_ICONS[option.iconKey] ?? AutoAwesomeRoundedIcon
                              return (
                                <Pressable
                                  key={`${option.value}-${option.labelKey}`}
                                  aria-pressed={draft.useCase === option.value}
                                  className={`setupChoiceCard ${draft.useCase === option.value ? 'is-active' : ''}`}
                                  onClick={() => handlePickUseCase(option.value)}
                                >
                                  <span className="setupChoiceCard__glyph" aria-hidden="true">
                                    <IconComp fontSize="inherit" />
                                  </span>
                                  <span className="setupChoiceCard__label">{t(option.labelKey)}</span>
                                  <span className="setupChoiceCard__hint">{t(option.hintKey)}</span>
                                </Pressable>
                              )
                            })}
                          </div>
                        )}

                        {activeStep === 'WHO' && (
                          <div className="setupWhoStep">
                            <div className="setupChoiceGrid setupChoiceGrid--cards setupChoiceGrid--three">
                              <Pressable
                                aria-pressed={draft.audience === 'SOLO'}
                                className={`setupChoiceCard setupChoiceCard--size ${draft.audience === 'SOLO' ? 'is-active' : ''}`}
                                onClick={() => handlePickWho('SOLO')}
                              >
                                <span className="setupChoiceCard__label setupChoiceCard__label--big">{t('NLSSetupWhoSoloLabel')}</span>
                                <span className="setupChoiceCard__hint">{t('NLSSetupWhoSoloHint')}</span>
                              </Pressable>
                              <Pressable
                                aria-pressed={draft.audience === 'SOLO_HELPERS'}
                                className={`setupChoiceCard setupChoiceCard--size ${draft.audience === 'SOLO_HELPERS' ? 'is-active' : ''}`}
                                onClick={() => handlePickWho('SOLO_HELPERS')}
                              >
                                <span className="setupChoiceCard__label setupChoiceCard__label--big">{t('NLSSetupWhoHelpersLabel')}</span>
                                <span className="setupChoiceCard__hint">{t('NLSSetupWhoHelpersHint')}</span>
                              </Pressable>
                              <Pressable
                                aria-pressed={draft.audience === 'TEAM'}
                                className={`setupChoiceCard setupChoiceCard--size ${draft.audience === 'TEAM' ? 'is-active' : ''}`}
                                onClick={() => handlePickWho('TEAM')}
                              >
                                <span className="setupChoiceCard__label setupChoiceCard__label--big">{t('NLSSetupWhoTeamLabel')}</span>
                                <span className="setupChoiceCard__hint">{t('NLSSetupWhoTeamHint')}</span>
                              </Pressable>
                            </div>

                            {draft.audience === 'TEAM' && (
                              <div className="setupSubOptions" style={{ marginTop: 20 }}>
                                <div className="setupChoiceGrid setupChoiceGrid--chips">
                                  {TEAM_SIZE_OPTIONS.map((option) => (
                                    <FilterChip
                                      key={option.value}
                                      selected={draft.teamSize === option.value}
                                      onClick={() => handlePickTeamSize(option.value)}
                                    >
                                      {t(option.labelKey)}
                                    </FilterChip>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {activeStep === 'FOCUS_AREAS' && (
                          <>
                            <div className="setupChoiceGrid setupChoiceGrid--chips">
                              {FOCUS_OPTIONS.map((option) => (
                                <FilterChip
                                  key={option.value}
                                  selected={draft.focusAreas.includes(option.value)}
                                  onClick={() => toggleFocusArea(option.value)}
                                >
                                  <span aria-hidden="true"><option.Icon fontSize="inherit" /></span> {t(option.labelKey)}
                                </FilterChip>
                              ))}
                            </div>
                            <p className="setupHelperNote">
                              {draft.focusAreas.length === 0
                                ? t('NLSSetupFocusSkipHint')
                                : t('NLSSetupFocusSelectedCount', { count: draft.focusAreas.length })}
                            </p>
                          </>
                        )}

                        {activeStep === 'APPEARANCE' && (
                          <div className="setupPrefsGrid">
                            <fieldset className="setupPrefsGroup">
                              <legend>{t('NLSSetupAppearanceThemeLegend')}</legend>
                              <div className="setupChoiceGrid setupChoiceGrid--cards setupChoiceGrid--three">
                                {THEME_OPTIONS.map((option) => (
                                  <Pressable
                                    key={option.value}
                                    aria-pressed={draft.theme === option.value}
                                    className={`setupChoiceCard setupChoiceCard--pref ${draft.theme === option.value ? 'is-active' : ''}`}
                                    onClick={() => updateDraft('theme', option.value)}
                                  >
                                    <span className={`setupThemeSwatch setupThemeSwatch--${option.value.toLowerCase()}`} aria-hidden="true" />
                                    <span className="setupChoiceCard__label">{t(option.labelKey)}</span>
                                    <span className="setupChoiceCard__hint">{t(option.hintKey)}</span>
                                  </Pressable>
                                ))}
                              </div>
                            </fieldset>
                          </div>
                        )}

                        {activeStep === 'EXTRAS' && (
                          <div className="setupToggleStack">
                            <ToggleRow
                              title={t('NLSSetupExtraAiTitle')}
                              hint={t('NLSSetupExtraAiHint')}
                              checked={draft.aiSuggestionsEnabled}
                              onChange={(v) => updateDraft('aiSuggestionsEnabled', v)}
                            />
                            <ToggleRow
                              title={t('NLSSetupExtraNotifTitle')}
                              hint={t('NLSSetupExtraNotifHint')}
                              checked={draft.notificationsEnabled}
                              onChange={(v) => updateDraft('notificationsEnabled', v)}
                            />
                          </div>
                        )}

                        {activeStep === 'WORKSPACE_NAME' && (
                          <form className="setupNameForm" onSubmit={handleNameSubmit}>
                            <label className="setupField">
                              <span>{t('NLSSetupNameDisplayNameLabel')}</span>
                              <TextInput
                                block
                                value={draft.workspaceName}
                                onChange={(next) => updateDraft('workspaceName', next)}
                                placeholder={t('NLSSetupNameDisplayNamePlaceholder')}
                                autoFocus
                                maxLength={64}
                              />
                            </label>
                            <label className="setupField">
                              <span>
                                {t('NLSSetupNameWorkspaceIdLabel')} <em>({t('NLSSetupNameWorkspaceIdPermanent')})</em>
                              </span>
                              <div className="setupSlugField">
                                <TextInput
                                  block
                                  value={draft.workspaceSlug}
                                  onChange={(next) => updateDraft('workspaceSlug', next)}
                                  placeholder={t('NLSSetupNameWorkspaceIdPlaceholder')}
                                  maxLength={40}
                                  aria-invalid={slugStatus === 'unavailable'}
                                  error={slugStatus === 'unavailable'}
                                  leadingIcon={<span className="setupSlugField__prefix">/</span>}
                                  trailingIcon={
                                    <span className={`setupSlugField__badge setupSlugField__badge--${slugStatus}`}>
                                      {slugStatus === 'checking' && '· · ·'}
                                      {slugStatus === 'available' && '✓'}
                                      {slugStatus === 'unavailable' && '!'}
                                    </span>
                                  }
                                />
                              </div>
                              <p className={`setupSlugHint setupSlugHint--${slugStatus}`}>
                                {slugMessage ?? t('NLSSetupSlugHintDefault')}
                              </p>
                            </label>
                            <ToggleRow
                              title={t('NLSSetupNamePublicToggleTitle')}
                              hint={t('NLSSetupNamePublicToggleHint')}
                              checked={draft.isPublic}
                              onChange={(v) => updateDraft('isPublic', v)}
                            />
                          </form>
                        )}
                      </div>

                      <SetupStepFooter onBack={goBack} backDisabled={stepIndex === 0}>
                        {activeStep === 'INTENT' && (
                          <>
                            <Button variant="tertiary" size="sm" onClick={advance}>
                              {t('NLSSetupActionSkip')}
                            </Button>
                            <Button
                              variant="primary"
                              trailingIcon={<ArrowForwardRoundedIcon />}
                              onClick={() => void handleFreeformSubmit()}
                              loading={isThinking}
                            >
                              {isThinking
                                ? t('NLSSetupActionThinking')
                                : freeformText.trim()
                                ? t('NLSSetupActionSend')
                                : t('NLSSetupActionContinue')}
                            </Button>
                          </>
                        )}

                        {(activeStep === 'ROLE' || activeStep === 'USE_CASE' || activeStep === 'WHO') && (
                          <Button variant="tertiary" size="sm" onClick={advance}>
                            {t('NLSSetupActionSkipThis')}
                          </Button>
                        )}

                        {activeStep === 'FOCUS_AREAS' && (
                          <>
                            <Button variant="tertiary" size="sm" onClick={advance}>
                              {t('NLSSetupActionSkip')}
                            </Button>
                            <Button variant="primary" trailingIcon={<ArrowForwardRoundedIcon />} onClick={advance}>
                              {t('NLSSetupActionContinue')}
                            </Button>
                          </>
                        )}

                        {activeStep === 'APPEARANCE' && (
                          <Button variant="primary" trailingIcon={<ArrowForwardRoundedIcon />} onClick={advance}>
                            {t('NLSSetupActionContinue')}
                          </Button>
                        )}

                        {activeStep === 'EXTRAS' && (
                          <Button variant="primary" trailingIcon={<ArrowForwardRoundedIcon />} onClick={advance}>
                            {setupKind === 'personal'
                              ? t('NLSSetupActionOpenPersonalSpace')
                              : t('NLSSetupActionContinue')}
                          </Button>
                        )}

                        {activeStep === 'WORKSPACE_NAME' && (
                          <Button
                            variant="primary"
                            trailingIcon={<ArrowForwardRoundedIcon />}
                            onClick={() => {
                              const form = document.querySelector<HTMLFormElement>('.setupNameForm')
                              form?.requestSubmit()
                            }}
                            disabled={!draft.workspaceName.trim() || slugStatus !== 'available'}
                          >
                            {t('NLSSetupActionOpenWorkspace')}
                          </Button>
                        )}
                      </SetupStepFooter>
                    </>
                  )}
                </motion.article>
              </AnimatePresence>
            </section>

            <aside className="setupReceipt" aria-label={t('NLSSetupReceiptAria')}>
              <div className="setupReceipt__seal" aria-hidden="true">
                <span>{workspaceInitial}</span>
              </div>
              <p className="setupReceipt__title">
                {draft.workspaceName.trim() ||
                  (setupKind === 'personal'
                    ? t('NLSSetupReceiptTitlePersonal')
                    : t('NLSSetupReceiptTitleUntitled'))}
              </p>
              <p className="setupReceipt__handle">
                {draft.workspaceSlug ? `/${draft.workspaceSlug}` : t('NLSSetupReceiptUrlPending')}
              </p>
              <dl className="setupReceipt__list">
                {setupKind === 'team' && (
                  <ReceiptRow label={t('NLSSetupReceiptRoleLabel')} value={draft.roleTitle || null} />
                )}
                <ReceiptRow
                  label={t('NLSSetupReceiptUseCaseLabel')}
                  value={
                    summary.useCaseOption ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <summary.useCaseIcon fontSize="inherit" /> {t(summary.useCaseOption.labelKey)}
                      </span>
                    ) : null
                  }
                />
                {setupKind === 'team' && (
                  <ReceiptRow label={t('NLSSetupReceiptCurrencyLabel')} value={draft.currency || detectLocaleCurrency()} />
                )}
                {setupKind === 'team' && (
                  <ReceiptRow
                    label={t('NLSSetupReceiptWhoLabel')}
                    value={
                      draft.audience === 'SOLO'
                        ? t('NLSSetupWhoSoloLabel')
                        : draft.audience === 'SOLO_HELPERS'
                        ? t('NLSSetupWhoHelpersLabel')
                        : draft.audience === 'TEAM'
                        ? summary.teamSizeOption
                          ? `${t('NLSSetupWhoTeamLabel')} (${t(summary.teamSizeOption.labelKey)})`
                          : t('NLSSetupWhoTeamLabel')
                        : summary.teamSizeOption
                        ? t(summary.teamSizeOption.labelKey)
                        : null
                    }
                  />
                )}
                <ReceiptRow
                  label={t('NLSSetupReceiptFocusLabel')}
                  pills={summary.focusLabels.map((f) => ({ key: f.value, label: t(f.labelKey), Icon: f.Icon }))}
                />
                <ReceiptRow
                  label={t('NLSSetupReceiptFeelLabel')}
                  pills={[{ key: 'theme', label: draft.theme.toLowerCase() }]}
                />
                {setupKind === 'team' && (
                  <ReceiptRow
                    label={t('NLSSetupReceiptExtrasLabel')}
                    pills={[
                      {
                        key: 'ai',
                        label: t('NLSSetupReceiptAiPill', {
                          state: draft.aiSuggestionsEnabled ? t('NLSSetupReceiptStateOn') : t('NLSSetupReceiptStateOff'),
                        }),
                        tone: draft.aiSuggestionsEnabled ? 'on' : 'off',
                      },
                      {
                        key: 'notif',
                        label: t('NLSSetupReceiptNotifPill', {
                          state: draft.notificationsEnabled ? t('NLSSetupReceiptStateOn') : t('NLSSetupReceiptStateOff'),
                        }),
                        tone: draft.notificationsEnabled ? 'on' : 'off',
                      },
                    ]}
                  />
                )}
              </dl>
              <p className="setupReceipt__foot">
                {setupKind === 'personal'
                  ? t('NLSSetupReceiptFootPersonal')
                  : t('NLSSetupReceiptFootTeam')}
              </p>
            </aside>
          </div>
        )}
      </motion.section>
    </main>
  )
}

type ToggleRowProps = {
  title: string
  hint: string
  checked: boolean
  onChange: (next: boolean) => void
}

const ToggleRow = ({ title, hint, checked, onChange }: ToggleRowProps) => (
  <div className={`setupToggleRow ${checked ? 'is-on' : ''}`}>
    <div className="setupToggleRow__text">
      <span className="setupToggleRow__title">{title}</span>
      <span className="setupToggleRow__hint">{hint}</span>
    </div>
    <Toggle checked={checked} onChange={onChange} aria-label={title} />
  </div>
)

type ReceiptPill = { key: string; label: string; Icon?: ComponentType<SvgIconProps>; tone?: 'on' | 'off' }

const ReceiptRow = ({ label, value, pills }: { label: string; value?: React.ReactNode; pills?: ReceiptPill[] }) => {
  const hasValue = value !== null && value !== undefined && value !== ''
  const hasPills = pills && pills.length > 0
  return (
    <div className="setupReceipt__row">
      <dt>{label}</dt>
      <dd>
        {hasPills ? (
          pills!.map((p) => (
            <span key={p.key} className={`setupReceipt__pill${p.tone ? ` setupReceipt__pill--${p.tone}` : ''}`}>
              {p.Icon ? <span aria-hidden="true"><p.Icon fontSize="inherit" /></span> : null}
              {p.label}
            </span>
          ))
        ) : hasValue ? (
          <span className="setupReceipt__pill">{value}</span>
        ) : (
          <span className="setupReceipt__muted">—</span>
        )}
      </dd>
    </div>
  )
}

const CompletionState = ({ setupKind, name }: { setupKind: 'personal' | 'team'; name: string }) => {
  const { t } = useNls()
  return (
    <div className="setupCompletion" role="status" aria-live="polite">
      <div className="setupCompletion__stage">
        <InteractiveConstellation
          className="setupCompletion__constellation"
          density={1400}
          linkDistance={110}
          interactionRadius={140}
          intensity={0.75}
          opacity={0.9}
        />
      </div>
      <p className="setupCompletion__eyebrow">
        {setupKind === 'personal'
          ? t('NLSSetupCompletionEyebrowPersonal')
          : t('NLSSetupCompletionEyebrowTeam')}
      </p>
      <h2 className="setupCompletion__title">
        {name?.trim()
          ? t('NLSSetupCompletionTitleNamed', { name: name.trim() })
          : t('NLSSetupCompletionTitleDefault')}
      </h2>
      <p className="setupCompletion__sub">
        {t('NLSSetupCompletionSub')}
      </p>
      <span className="setupThinkingDots" aria-hidden="true">
        <span /><span /><span />
      </span>
    </div>
  )
}
